import styled from 'styled-components';
import { FormikErrors, FormikTouched, FormikValues } from 'formik';

import {
  BODY_SHAPES,
  BODY_SHAPE_OPTIONS,
  HEIGHT_MEASUREMENT,
} from 'productTemplate/enums/fitFeedback';
import { Choice } from 'shared/components/Choice';
import { FeatureFlags } from 'featureFlags/constants';
import { FitFeedbackFormActions } from 'productTemplate/state/fitFeedbackFormReducer';
import { FitFeedbackFormUserHeight } from 'productTemplate/components/fitFeedback/FitFeedbackFormUserHeight';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { variableFontRegular } from 'styles/theme/text';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import {
  useFitFeedbackFormDispatch,
  useFitFeedbackFormState,
} from 'productTemplate/providers/FitFeedbackProvider';
import { t } from 'localization/utils/t';
import { getTranslations } from 'shared/utils/getTranslations';

const TP = 'productTemplate.components.fitFeedback.FitFeedbackFormUserInfo';

interface IFitFeedbackFormUserInfo {
  setFieldValue: (key: string, value: string) => void;
  errors: FormikErrors<{ [key: string]: string }>;
  onBlur: (e) => void;
  touched: FormikTouched<FormikValues>;
}

export const FitFeedbackFormUserInfo = ({
  setFieldValue,
  errors,
  onBlur,
  touched,
}: IFitFeedbackFormUserInfo) => {
  const dispatch = useFitFeedbackFormDispatch();
  const { heightMeasurement } = useFitFeedbackFormState();
  const { bodyShape, heightFeet, heightInches, heightCm } = useFitFeedbackFormState();
  const showCm = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_FIT_FEEDBACK_CM);
  const heightTouched = touched?.heightCm || touched?.heightFeet || touched?.heightInches;
  const heightError = errors?.heightCm || errors?.heightFeet || errors?.heightInches;
  const bodyShapeError = touched?.bodyShape && errors?.bodyShape;
  const bodyShapeOptions = getTranslations(BODY_SHAPE_OPTIONS);

  const handleBodyShapeChange = (value) => {
    setFieldValue('bodyShape', value);
    dispatch({
      type: FitFeedbackFormActions.setBodyShape,
      payload: value,
    });
  };

  const handleHeightMeasurementChange = (value) => () => {
    setFieldValue('heightMeasurement', value);
    if (value === HEIGHT_MEASUREMENT.FT) {
      setFieldValue('heightCm', '');
    } else {
      setFieldValue('heightFeet', '');
      setFieldValue('heightInches', '');
    }
    dispatch({
      type: FitFeedbackFormActions.setHeightMeasurement,
      payload: value,
    });
  };

  const handleOnChange = (e, action) => {
    const { value, name } = e.target;
    setFieldValue(name, value);
    dispatch({
      type: FitFeedbackFormActions[action],
      payload: value,
    });
  };

  return (
    <FitFeedbackFormUserInfo.Wrapper>
      <FitFeedbackFormUserInfo.Header>{t(`${TP}.height`, 'Height')}</FitFeedbackFormUserInfo.Header>
      <FitFeedbackFormUserInfo.Flex>
        {heightMeasurement === HEIGHT_MEASUREMENT.FT ? (
          <>
            <FitFeedbackFormUserHeight
              measurement={HEIGHT_MEASUREMENT.FT}
              name="heightFeet"
              onBlur={onBlur}
              onChange={(e) => handleOnChange(e, FitFeedbackFormActions.setHeightFeet)}
              value={heightFeet}
            />
            <FitFeedbackFormUserHeight
              measurement={HEIGHT_MEASUREMENT.IN}
              name="heightInches"
              onBlur={onBlur}
              onChange={(e) => handleOnChange(e, FitFeedbackFormActions.setHeightInches)}
              value={heightInches}
            />
          </>
        ) : (
          <FitFeedbackFormUserHeight
            measurement={HEIGHT_MEASUREMENT.CM}
            name="heightCm"
            onBlur={onBlur}
            onChange={(e) => handleOnChange(e, FitFeedbackFormActions.setHeightCm)}
            value={heightCm}
          />
        )}
        {showCm && (
          <>
            <FitFeedbackFormUserInfo.HeightMeasurement
              $isSelected={heightMeasurement === HEIGHT_MEASUREMENT.FT}
              onClick={handleHeightMeasurementChange(HEIGHT_MEASUREMENT.FT)}
              data-qa="height_measurement_feet"
            >
              {t(`${TP}.feet`, 'FT')}
            </FitFeedbackFormUserInfo.HeightMeasurement>
            <FitFeedbackFormUserInfo.HeightMeasurement
              $isSelected={heightMeasurement === HEIGHT_MEASUREMENT.CM}
              onClick={handleHeightMeasurementChange(HEIGHT_MEASUREMENT.CM)}
              data-qa="height_measurement_cm"
            >
              {t(`${TP}.cm`, 'CM')}
            </FitFeedbackFormUserInfo.HeightMeasurement>
          </>
        )}
      </FitFeedbackFormUserInfo.Flex>
      {heightError && heightTouched && (
        <FitFeedbackFormUserInfo.Error>{heightError}</FitFeedbackFormUserInfo.Error>
      )}

      <FitFeedbackFormUserInfo.Header>
        {t(`${TP}.bodyShape`, 'Body Shape')}
      </FitFeedbackFormUserInfo.Header>
      <FitFeedbackFormUserInfo.BodyShapeWrapper>
        {Object.values(bodyShapeOptions).map(({ component: Component, value, text }) => {
          return (
            <FitFeedbackFormUserInfo.BodyShapeSelection
              checked={bodyShape === value}
              key={value}
              label={
                <>
                  <Component
                    isFilled={bodyShape === value}
                    isInverted={value === BODY_SHAPES.INVERTED_TRIANGLE}
                  />
                  <FitFeedbackFormUserInfo.BodyShapeText>
                    {text}
                  </FitFeedbackFormUserInfo.BodyShapeText>
                </>
              }
              name="bodyShape"
              onChange={() => handleBodyShapeChange(value)}
              type="radio"
              value={value}
            />
          );
        })}
      </FitFeedbackFormUserInfo.BodyShapeWrapper>
      {bodyShapeError && (
        <FitFeedbackFormUserInfo.Error>{bodyShapeError}</FitFeedbackFormUserInfo.Error>
      )}
    </FitFeedbackFormUserInfo.Wrapper>
  );
};

FitFeedbackFormUserInfo.Wrapper = styled.div``;

FitFeedbackFormUserInfo.Header = styled.div`
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  margin: 16px 0;
`;

FitFeedbackFormUserInfo.Flex = styled.div`
  display: flex;
  align-items: center;
`;

FitFeedbackFormUserInfo.HeightMeasurement = styled.div<{ $isSelected?: boolean }>`
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
  padding: 0 4px;
  text-decoration: ${({ $isSelected }) => $isSelected && 'underline'};
  cursor: pointer;
`;

FitFeedbackFormUserInfo.BodyShapeText = styled.span`
  margin-left: 9px;
`;

FitFeedbackFormUserInfo.BodyShapeWrapper = styled.div`
  display: flex;
  overflow-x: scroll;
  margin: 0 -16px 16px;
  ${hideScrollbars};
`;

FitFeedbackFormUserInfo.BodyShapeSelection = styled(Choice)`
  min-width: unset;
  margin-right: 8px;

  &:first-child {
    margin-left: 12px;
  }
`;

FitFeedbackFormUserInfo.Error = styled.div`
  color: ${({ theme }) => theme.colors.red};
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
  margin: 12px 0;
`;
